// Onglet « Parties » d'un dossier, lu comme un litige : nos clients à gauche,
// la partie adverse à droite avec son conseil rattaché, puis la juridiction et
// les tiers. Un clic sur une carte ouvre sa fiche courte dans la colonne droite.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { Spinner } from '../ui/Spinner';
import {
  useContactDossiers,
  useDossierContacts,
  useSetDossierContactRepresents,
  useUnlinkContactFromDossier,
} from '../../hooks/queries/useContacts';
import {
  type Contact,
  type DossierContactRole,
  type DossierContactWithContact,
  DOSSIER_CONTACT_ROLE_LABELS,
  getContactDisplayName,
} from '../../services/contacts/contactService';
import { useUpdateDossier } from '../../hooks/queries/useDossiers';
import type { Dossier } from '../../services/dossiers/dossierService';
import {
  normalizeProceduralRole,
  PROCEDURAL_ROLE_SUGGESTIONS,
} from '../../lib/dossiers/proceduralRoles';
import { formatAppDate } from '../../lib/dateFormat';
import { InviteClientButton } from '../portal/InviteClientButton';
import { AddPartyModal } from './DossierPartiesSection';

interface Props {
  dossier: Dossier;
  canEdit: boolean;
}

type PartyLink = DossierContactWithContact;

const CAMP_ROLES: DossierContactRole[] = ['client', 'local_counsel', 'opposing_party', 'opposing_counsel'];

const linkKey = (link: PartyLink) => `${link.contact_id}-${link.role}`;

function initials(contact: Contact): string {
  return ((contact.first_name?.[0] ?? '') + (contact.last_name?.[0] ?? '')).toUpperCase() || '?';
}

export function DossierPartiesTab({ dossier, canEdit }: Props) {
  const dossierId = dossier.id;
  const updateDossier = useUpdateDossier();
  const { data: links, isLoading } = useDossierContacts(dossierId);
  const unlink = useUnlinkContactFromDossier();
  const [addRole, setAddRole] = useState<DossierContactRole | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const all = useMemo(() => (links ?? []).filter((l) => l.contact), [links]);
  const byRole = (role: DossierContactRole) => all.filter((l) => l.role === role);
  const clients = byRole('client');
  const localCounsel = byRole('local_counsel');
  const opposing = byRole('opposing_party');
  const opposingCounsel = byRole('opposing_counsel');
  const tiers = all.filter((l) => !CAMP_ROLES.includes(l.role));

  // Un conseil adverse se range sous la partie qu'il représente. Avec une seule
  // partie adverse, il s'y range même sans rattachement enregistré.
  const counselOf = (party: PartyLink) =>
    opposingCounsel.filter(
      (c) =>
        c.represents_contact_id === party.contact_id ||
        (opposing.length === 1 && !opposing.some((p) => p.contact_id === c.represents_contact_id)),
    );
  const looseCounsel = opposingCounsel.filter(
    (c) => !opposing.some((party) => counselOf(party).includes(c)),
  );

  const selected = all.find((l) => linkKey(l) === selectedKey) ?? clients[0] ?? all[0] ?? null;

  function saveQuality(column: 'client_procedural_role' | 'opposing_procedural_role', next: string | null) {
    updateDossier.mutate(
      { id: dossierId, patch: { [column]: next } },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer'),
      },
    );
  }

  function remove(link: PartyLink) {
    if (!window.confirm(`Retirer ${getContactDisplayName(link.contact)} de ce dossier ?`)) return;
    unlink.mutate({ dossierId, contactId: link.contact_id, role: link.role });
  }

  const card = (link: PartyLink, sub?: string) => (
    <PartyCard
      key={linkKey(link)}
      link={link}
      sub={sub}
      selected={selected !== null && linkKey(selected) === linkKey(link)}
      canEdit={canEdit}
      onSelect={() => setSelectedKey(linkKey(link))}
      onRemove={() => remove(link)}
    />
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="flex min-w-0 flex-col gap-3.5">
        <div className="grid grid-cols-1 items-start gap-3.5 lg:grid-cols-2">
          <Camp
            title="Clients"
            side="ours"
            quality={dossier.client_procedural_role}
            canEdit={canEdit}
            onSaveQuality={(next) => saveQuality('client_procedural_role', next)}
          >
            {clients.map((link) => card(link, link.is_primary ? 'Client principal' : undefined))}
            <Rep label="Avocat postulant">
              {localCounsel.map((link) => card(link))}
              {canEdit && localCounsel.length === 0 ? (
                <AddSlot
                  title="Avocat local qui représente le cabinet devant une juridiction hors de son ressort"
                  onClick={() => setAddRole('local_counsel')}
                >
                  Ajouter un avocat postulant
                </AddSlot>
              ) : null}
            </Rep>
            {canEdit ? <AddSlot onClick={() => setAddRole('client')}>Ajouter un client</AddSlot> : null}
          </Camp>

          <Camp
            title="Partie adverse"
            side="theirs"
            quality={dossier.opposing_procedural_role}
            canEdit={canEdit}
            onSaveQuality={(next) => saveQuality('opposing_procedural_role', next)}
          >
            {opposing.map((party) => (
              <div key={linkKey(party)} className="flex flex-col gap-2">
                {card(party)}
                {counselOf(party).length > 0 ? (
                  <Rep label="Représentée par">{counselOf(party).map((link) => card(link))}</Rep>
                ) : null}
              </div>
            ))}
            {looseCounsel.length > 0 ? (
              <Rep label="Conseil adverse">{looseCounsel.map((link) => card(link))}</Rep>
            ) : null}
            {canEdit ? (
              <div className="flex gap-2">
                <AddSlot onClick={() => setAddRole('opposing_party')}>Ajouter une partie adverse</AddSlot>
                <AddSlot onClick={() => setAddRole('opposing_counsel')}>Ajouter son conseil</AddSlot>
              </div>
            ) : null}
          </Camp>
        </div>

        <section className="rounded-xl border border-neutral-200 bg-white">
          <header className="flex items-center justify-between border-b border-[#e6e2d8] px-4 py-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
              Juridiction &amp; tiers
            </h3>
            {canEdit ? (
              <button
                type="button"
                onClick={() => setAddRole('court')}
                className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium text-cime-text-primary hover:bg-neutral-100"
              >
                <img src="/icons/plus.svg" alt="" className="h-3 w-3" />
                Ajouter un tiers
              </button>
            ) : null}
          </header>
          {tiers.length === 0 ? (
            <p className="px-4 py-5 text-[13px] text-neutral-400">
              {dossier.juridiction
                ? `Juridiction du dossier : ${dossier.juridiction}. Aucun tiers ajouté (tribunal, expert, commissaire de justice…).`
                : 'Aucun tiers ajouté (tribunal, expert, commissaire de justice…).'}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2.5 p-3 md:grid-cols-2 xl:grid-cols-3">
              {tiers.map((link) => (
                <TierCard
                  key={linkKey(link)}
                  link={link}
                  selected={selected !== null && linkKey(selected) === linkKey(link)}
                  onSelect={() => setSelectedKey(linkKey(link))}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {selected ? (
        <PartyFiche
          key={linkKey(selected)}
          link={selected}
          dossierId={dossierId}
          opposing={opposing}
          canEdit={canEdit}
          onRemove={() => remove(selected)}
        />
      ) : (
        <aside className="rounded-xl border border-dashed border-neutral-200 bg-white px-4 py-8 text-center text-[13px] text-neutral-400">
          Aucune partie liée à ce dossier.
        </aside>
      )}

      {addRole && (
        <AddPartyModal
          dossierId={dossierId}
          defaultRole={addRole}
          existingIds={new Set(all.map(linkKey))}
          onClose={() => setAddRole(null)}
        />
      )}
    </div>
  );
}

// ─── Camp ────────────────────────────────────────────────────────────────────

function Camp({
  title,
  side,
  quality,
  canEdit,
  onSaveQuality,
  children,
}: {
  title: string;
  side: 'ours' | 'theirs';
  quality: string | null;
  canEdit: boolean;
  onSaveQuality: (next: string | null) => void;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        'flex flex-col gap-2 rounded-xl border border-neutral-200 border-t-2 bg-white p-3',
        side === 'ours' ? 'border-t-cime-primary' : 'border-t-[#c8431f]',
      )}
    >
      <div className="flex items-center justify-between gap-3 px-1 pb-1">
        <h3
          className="text-[17px] font-semibold text-cime-text-primary"
        >
          {title}
        </h3>
        <SideQuality key={quality ?? ''} value={quality} canEdit={canEdit} onSave={onSaveQuality} />
      </div>
      {children}
    </section>
  );
}

function SideQuality({
  value,
  canEdit,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  onSave: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  if (!canEdit) {
    return value ? (
      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-cime-text-muted">{value}</span>
    ) : null;
  }
  function commit() {
    const next = normalizeProceduralRole(draft);
    if (next !== (value ?? null)) onSave(next);
  }
  return (
    <>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        list="procedural-role-suggestions"
        placeholder="Qualité…"
        aria-label="Qualité procédurale"
        title="Qualité procédurale"
        className="h-7 w-40 rounded-md border border-transparent bg-transparent px-2 text-right text-[11px] font-bold uppercase tracking-[0.08em] text-cime-text-muted placeholder:font-medium placeholder:normal-case placeholder:tracking-normal placeholder:text-neutral-400 hover:border-neutral-200 focus:border-cime-primary focus:bg-white focus:outline-none"
      />
      <datalist id="procedural-role-suggestions">
        {PROCEDURAL_ROLE_SUGGESTIONS.map((label) => (
          <option key={label} value={label} />
        ))}
      </datalist>
    </>
  );
}

function Rep({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="ml-4 flex flex-col gap-2 border-l border-dashed border-neutral-300 pl-3.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">{label}</span>
      {children}
    </div>
  );
}

function AddSlot({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex min-h-9 flex-1 flex-shrink-0 items-center justify-center gap-2 rounded-[10px] border border-dashed border-neutral-300 text-[13px] text-cime-text-muted transition-colors hover:border-cime-primary hover:text-cime-primary"
    >
      <img src="/icons/plus.svg" alt="" className="h-3 w-3 opacity-60" />
      {children}
    </button>
  );
}

// ─── Cartes ──────────────────────────────────────────────────────────────────

function Mark({ contact, large }: { contact: Contact; large?: boolean }) {
  const size = large ? 'h-12 w-12' : 'h-9 w-9';
  if (contact.kind === 'corporate') {
    return (
      <span className={cn('grid flex-shrink-0 place-items-center rounded-[10px] bg-[#f1efe8]', size)}>
        <img src="/icons/building.svg" alt="" className={large ? 'h-5 w-5' : 'h-4 w-4'} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'grid flex-shrink-0 place-items-center rounded-full bg-[#f1efe8] font-semibold text-cime-text-primary',
        size,
        large ? 'text-sm' : 'text-[11px]',
      )}
    >
      {initials(contact)}
    </span>
  );
}

function PartyCard({
  link,
  sub,
  selected,
  canEdit,
  onSelect,
  onRemove,
}: {
  link: PartyLink;
  sub?: string;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  const { contact } = link;
  const place = [contact.postal_code, contact.city].filter(Boolean).join(' ');
  return (
    <article
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'group flex cursor-pointer items-start gap-3 rounded-[10px] border bg-white p-3 text-left transition-colors',
        selected
          ? 'border-cime-primary shadow-[0_0_0_1px_var(--cime-primary)]'
          : 'border-neutral-200 hover:border-cime-primary/40',
      )}
    >
      <Mark contact={contact} />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="m-0 flex items-center gap-1.5 truncate text-sm font-semibold text-cime-text-primary">
          <span className="truncate">{getContactDisplayName(contact)}</span>
          {link.is_primary ? (
            <img src="/icons/star.svg" alt="Partie principale" title="Partie principale" className="h-3 w-3 flex-shrink-0" />
          ) : null}
        </p>
        <p className="m-0 mt-0.5 truncate text-xs text-cime-text-muted">
          {sub ?? (place || DOSSIER_CONTACT_ROLE_LABELS[link.role])}
        </p>
        <div className="mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-cime-text-muted">
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <img src="/icons/envelope.svg" alt="" className="h-3 w-3 flex-shrink-0 opacity-50" />
            {contact.email ? (
              <a
                href={`mailto:${contact.email}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate hover:text-cime-primary"
              >
                {contact.email}
              </a>
            ) : (
              <span className="text-neutral-400">non renseigné</span>
            )}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <img src="/icons/phone.svg" alt="" className="h-3 w-3 flex-shrink-0 opacity-50" />
            {contact.phone ? (
              <a href={`tel:${contact.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-cime-primary">
                {contact.phone}
              </a>
            ) : (
              <span className="text-neutral-400">non renseigné</span>
            )}
          </span>
        </div>
      </div>
      {canEdit ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Retirer la partie"
          title="Retirer du dossier"
          className="flex-shrink-0 rounded p-1 opacity-0 transition hover:bg-red-50 group-hover:opacity-60 group-hover:hover:opacity-100 focus:opacity-100"
        >
          <img src="/icons/trash-can.svg" alt="" className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </article>
  );
}

function TierCard({
  link,
  selected,
  onSelect,
}: {
  link: PartyLink;
  selected: boolean;
  onSelect: () => void;
}) {
  const { contact } = link;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex items-start gap-2.5 rounded-[10px] border bg-[#fbfaf6] p-3 text-left transition-colors',
        selected ? 'border-cime-primary' : 'border-[#e6e2d8] hover:border-cime-primary/40',
      )}
    >
      <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg border border-[#e6e2d8] bg-white">
        <img
          src={link.role === 'court' ? '/icons/building-columns.svg' : '/icons/address-book.svg'}
          alt=""
          className="h-3.5 w-3.5 opacity-70"
        />
      </span>
      <span className="min-w-0 leading-tight">
        <span className="block text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
          {DOSSIER_CONTACT_ROLE_LABELS[link.role]}
        </span>
        <b className="block truncate text-[13px] font-semibold text-cime-text-primary">
          {getContactDisplayName(contact)}
        </b>
        <small className="block truncate text-xs text-cime-text-muted">
          {contact.phone || contact.email || contact.city || '—'}
        </small>
      </span>
    </button>
  );
}

// ─── Fiche courte ────────────────────────────────────────────────────────────

function PartyFiche({
  link,
  dossierId,
  opposing,
  canEdit,
  onRemove,
}: {
  link: PartyLink;
  dossierId: string;
  opposing: PartyLink[];
  canEdit: boolean;
  onRemove: () => void;
}) {
  const { contact } = link;
  const { data: dossiers } = useContactDossiers(contact.id);
  const setRepresents = useSetDossierContactRepresents();
  const others = (dossiers ?? []).filter((d) => d.dossier_id !== dossierId && d.dossier);
  const [othersOpen, setOthersOpen] = useState(false);

  return (
    <aside className="rounded-xl border border-neutral-200 bg-white">
      <div className="flex flex-col items-center px-4 pb-4 pt-5 text-center">
        <Mark contact={contact} large />
        <h3
          className="mt-3 text-[17px] font-semibold leading-tight text-cime-text-primary"
        >
          {getContactDisplayName(contact)}
        </h3>
        <p className="mt-1 text-xs text-cime-text-muted">
          {DOSSIER_CONTACT_ROLE_LABELS[link.role]} · au répertoire depuis le {formatAppDate(contact.created_at)}
        </p>
      </div>

      <FicheSection>
        <FicheLine icon="/icons/envelope.svg">
          {contact.email ? (
            <a href={`mailto:${contact.email}`} className="truncate hover:text-cime-primary">
              {contact.email}
            </a>
          ) : (
            <span className="text-neutral-400">E-mail non renseigné</span>
          )}
        </FicheLine>
        <FicheLine icon="/icons/phone.svg">
          {contact.phone ? (
            <a href={`tel:${contact.phone}`} className="hover:text-cime-primary">
              {contact.phone}
            </a>
          ) : (
            <span className="text-neutral-400">Téléphone non renseigné</span>
          )}
        </FicheLine>
        <Link
          to={`/contacts/${contact.id}`}
          className="mt-1 flex h-8 items-center justify-between gap-2 rounded-lg px-2 text-[13px] font-medium text-cime-text-primary hover:bg-neutral-100"
        >
          <span className="inline-flex items-center gap-2">
            <img src="/icons/address-book.svg" alt="" className="h-3.5 w-3.5 opacity-70" />
            Voir la fiche dans le répertoire
          </span>
          <img src="/icons/chevron-right.svg" alt="" className="h-2.5 w-2.5 opacity-50" />
        </Link>
      </FicheSection>

      {link.role === 'opposing_counsel' && opposing.length > 0 ? (
        <FicheSection title="Représente">
          <select
            aria-label="Partie représentée"
            disabled={!canEdit || setRepresents.isPending}
            value={link.represents_contact_id ?? ''}
            onChange={(e) =>
              setRepresents.mutate(
                {
                  dossierId,
                  contactId: link.contact_id,
                  role: link.role,
                  representsContactId: e.target.value || null,
                },
                {
                  onError: (err) =>
                    toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer'),
                },
              )
            }
            className="h-9 w-full rounded-lg border border-neutral-300 bg-white px-2 text-[13px] text-cime-text-primary focus:border-cime-primary focus:outline-none disabled:opacity-60"
          >
            <option value="">{opposing.length === 1 ? 'La partie adverse (par défaut)' : 'Non rattaché'}</option>
            {opposing.map((party) => (
              <option key={party.contact_id} value={party.contact_id}>
                {getContactDisplayName(party.contact)}
              </option>
            ))}
          </select>
        </FicheSection>
      ) : null}

      {link.role === 'client' ? (
        <FicheSection title="Portail client">
          <InviteClientButton contactId={contact.id} dossierId={dossierId} compact />
        </FicheSection>
      ) : null}

      <FicheSection>
        <button
          type="button"
          onClick={() => setOthersOpen((open) => !open)}
          aria-expanded={othersOpen}
          className="flex w-full items-center gap-2 text-left"
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">
            Autres dossiers au cabinet
          </span>
          <span className="rounded-full bg-neutral-100 px-1.5 text-[11px] font-bold tabular-nums text-cime-text-muted">
            {others.length}
          </span>
          <img
            src="/icons/chevron-right.svg"
            alt=""
            className={cn('ml-auto h-2.5 w-2.5 opacity-50 transition-transform', othersOpen && 'rotate-90')}
          />
        </button>
        {othersOpen ? (
          others.length === 0 ? (
            <p className="mt-2 text-xs text-neutral-400">Aucun autre dossier.</p>
          ) : (
            <div className="mt-2 flex flex-col">
              {others.map((other) => (
                <Link
                  key={`${other.dossier_id}-${other.role}`}
                  to={`/dossiers/${other.dossier_id}`}
                  className="flex items-center justify-between gap-2 rounded-md px-1.5 py-1.5 text-[13px] text-cime-text-primary hover:bg-neutral-100"
                >
                  <span className="truncate">{other.dossier.title}</span>
                  <span className="flex-shrink-0 text-[11px] text-neutral-400">{other.dossier.number}</span>
                </Link>
              ))}
            </div>
          )
        ) : null}
      </FicheSection>

      {canEdit ? (
        <FicheSection>
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-2 text-xs text-[#c8431f] hover:underline"
          >
            <img src="/icons/trash-can.svg" alt="" className="h-3 w-3" />
            Retirer du dossier
          </button>
        </FicheSection>
      ) : null}
    </aside>
  );
}

function FicheSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-[#e6e2d8] px-4 py-3">
      {title ? (
        <h4 className="mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400">{title}</h4>
      ) : null}
      {children}
    </div>
  );
}

function FicheLine({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5 py-1 text-[13px] text-cime-text-primary">
      <img src={icon} alt="" className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
      {children}
    </div>
  );
}
